/**
 * macro-sections — the six section kinds of the Macro and Calendar layouts
 * (`docs/plans/macro.md`; mock `docs/mockups/macro-layout.html`).
 *
 * Pure: which kinds exist, the config type each carries, how a section of one
 * is made, and the two layouts built from them. What a section *draws* is the
 * registry's (`components/macro/registry.ts`).
 *
 * **Admin only** until the owner has reviewed them (macro.md, *Who sees it*):
 * offered in the layout browser only to an admin, and drawn as a refusal for
 * anyone else who holds one (`components/macro/MacroSectionHost.tsx`).
 *
 * Every config is all-optional and absent means the mock's default, so `{}` is
 * a whole, valid config — which is why `Section.config` is an `anyOf`.
 */

import type {
  CalendarSectionConfig,
  HavensConfig,
  Layout,
  MacroReleasesConfig,
  PositioningConfig,
  RelationshipsConfig,
  Section,
  YieldCurveConfig,
} from "@qufolio/model";
import { mintUlid } from "@qufolio/model";

/** The kinds, in the Macro layout's order, Calendar last (macro.md, *Decided*). */
export const MACRO_KINDS = [
  "macroReleases",
  "yieldCurve",
  "relationships",
  "havens",
  "positioning",
  "calendar",
] as const;

export type MacroKind = (typeof MACRO_KINDS)[number];

/** The config each kind persists — what a section builder reads and writes. */
export interface MacroConfigByKind {
  macroReleases: MacroReleasesConfig;
  yieldCurve: YieldCurveConfig;
  relationships: RelationshipsConfig;
  havens: HavensConfig;
  positioning: PositioningConfig;
  calendar: CalendarSectionConfig;
}

export type MacroSectionConfig = MacroConfigByKind[MacroKind];

const KINDS: ReadonlySet<string> = new Set(MACRO_KINDS);

export function isMacroKind(kind: string): kind is MacroKind {
  return KINDS.has(kind);
}

/** Each kind's title, as the section header and + Add section show it. */
export const MACRO_KIND_TITLE: Record<MacroKind, string> = {
  macroReleases: "Releases",
  yieldCurve: "Yield curve, linked",
  relationships: "What moves together",
  havens: "Safe-haven correlations",
  positioning: "Positioning & liquidity",
  calendar: "Calendar",
};

/** One line on what each kind holds, for + Add section. */
export const MACRO_KIND_BLURB: Record<MacroKind, string> = {
  macroReleases: "The releases, grouped: growth, inflation, labor, policy, sentiment",
  yieldCurve: "Curve, spreads and heat, with markets and the economy on the same clock",
  relationships: "Correlation matrix, and a pair's scatter with β and rolling correlation",
  havens: "Stocks vs bonds, bonds vs gold, bonds vs dollar — does each still hedge?",
  positioning: "Funding stress over the crowded long / short spread, one time axis",
  calendar: "Economic + earnings, week or month, list or grid",
};

/**
 * The Macro layout, row by row (owner, 2026-11-02): releases, the curve, what
 * moves together, then Safe-haven correlations beside positioning. A row of two
 * is drawn side by side in the stacked layout (`stackedRows`), ordered by column.
 */
export const MACRO_LAYOUT_ROWS: readonly (readonly MacroKind[])[] = [
  ["macroReleases"],
  ["yieldCurve"],
  ["relationships"],
  ["havens", "positioning"],
];

/**
 * The section's config if it is one of the six, `undefined` otherwise. A
 * missing or non-object config reads as `{}` — every field's default — so a
 * builder never has to tell "unset" from "absent". A `relationships` config
 * saved before `havens` was split out carries nothing havens needs, so it
 * reads as it always did.
 */
export function macroConfig<K extends MacroKind>(
  section: Section,
  kind: K,
): MacroConfigByKind[K] | undefined {
  if (section.kind !== kind) return undefined;
  const config = section.config as unknown;
  return (config && typeof config === "object" && !Array.isArray(config)
    ? config
    : {}) as MacroConfigByKind[K];
}

/** A fresh section of `kind` at `row`, spanning `columns`, at `column`. */
export function macroSection(kind: MacroKind, row: number, columns = 1, column = 0): Section {
  return {
    id: mintUlid(),
    kind,
    row,
    column,
    ...(columns > 1 ? { columnSpan: columns } : {}),
    title: MACRO_KIND_TITLE[kind],
    config: {},
  };
}

/** A layout of `rows`, each row's kinds side by side; a lone kind spans every column. */
function rowsLayout(name: string, rows: readonly (readonly MacroKind[])[]): Layout {
  const columns = Math.max(1, ...rows.map((r) => r.length));
  return {
    id: mintUlid(),
    name,
    rows: rows.length,
    columns,
    rowSizes: rows.map(() => "1fr"),
    columnSizes: Array.from({ length: columns }, () => "1fr"),
    sections: rows.flatMap((kinds, row) =>
      kinds.map((kind, column) =>
        macroSection(kind, row, kinds.length === 1 ? columns : 1, column),
      ),
    ),
  };
}

/** The Macro layout: `MACRO_LAYOUT_ROWS`. */
export function macroLayout(name: string): Layout {
  return rowsLayout(name, MACRO_LAYOUT_ROWS);
}

/** The Calendar layout: one `calendar` section. */
export function calendarLayout(name: string): Layout {
  return rowsLayout(name, [["calendar"]]);
}

/**
 * The kinds + Add section offers for `layout`: each of the six once — the mock
 * shows one already in the layout as "already in this layout", not twice.
 */
export function macroKindsToAdd(layout: Pick<Layout, "sections">): MacroKind[] {
  const present = new Set(layout.sections.map((s) => s.kind));
  return MACRO_KINDS.filter((kind) => !present.has(kind));
}

/**
 * `layout` with a `kind` section appended as a new bottom row spanning every
 * column — the mock's + Add section, which adds below what is there. The new
 * row takes an even share (`1fr`). Unchanged if the layout already holds one.
 *
 * `havens` is the exception (owner, 2026-11-02): with `positioning` alone on its
 * row it goes beside it, on the left, as in the default layout — so a Macro
 * layout saved before the split gains it where a new one has it.
 */
export function withMacroSection(layout: Layout, kind: MacroKind): Layout {
  if (layout.sections.some((s) => s.kind === kind)) return layout;
  if (kind === "havens") {
    const beside = besidePositioning(layout);
    if (beside) return beside;
  }
  const rows = Math.max(
    layout.rows,
    ...layout.sections.map((s) => s.row + (s.rowSpan ?? 1)),
  );
  const columns = Math.max(1, layout.columns);
  const rowSizes = Array.from({ length: rows }, (_, i) => layout.rowSizes?.[i] ?? "1fr");
  return {
    ...layout,
    rows: rows + 1,
    rowSizes: [...rowSizes, "1fr"],
    sections: [...layout.sections, macroSection(kind, rows, columns)],
  };
}

/**
 * `havens` beside a `positioning` that is alone on its row (column 0, and
 * positioning moves to column 1), or `undefined` when it is not alone. The grid
 * grows to two columns if it had one, and the sections that spanned the old
 * width keep spanning the new one.
 */
function besidePositioning(layout: Layout): Layout | undefined {
  const pos = layout.sections.find((s) => s.kind === "positioning");
  if (!pos) return undefined;
  if (layout.sections.some((s) => s !== pos && s.row === pos.row)) return undefined;
  const old = Math.max(1, layout.columns);
  const columns = Math.max(2, old);
  const columnSizes = Array.from({ length: columns }, (_, i) => layout.columnSizes?.[i] ?? "1fr");
  const sections = layout.sections.map((s): Section => {
    if (s === pos) {
      const { columnSpan: _span, ...rest } = s;
      void _span;
      return { ...rest, column: 1 };
    }
    return (s.columnSpan ?? 1) >= old && s.column === 0 && columns > old
      ? { ...s, columnSpan: columns }
      : s;
  });
  return {
    ...layout,
    columns,
    columnSizes,
    sections: [...sections, macroSection("havens", pos.row, 1, 0)],
  };
}

/**
 * A stacked layout's rows (owner, 2026-11-02): the sections grouped by `row`,
 * rows top to bottom, each row's sections left to right by `column`. A row of
 * one is drawn full width; a row of two or more, side by side.
 */
export function stackedRows(sections: readonly Section[]): Section[][] {
  const byRow = new Map<number, Section[]>();
  for (const s of sections) {
    const row = byRow.get(s.row);
    if (row) row.push(s);
    else byRow.set(s.row, [s]);
  }
  return [...byRow.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, row]) => row.sort((a, b) => a.column - b.column));
}

/**
 * Two or more sections, every one a Macro kind: `SectionGrid` stacks them at
 * their natural height under one scrollbar (owner, 2026-11-01). A lone section
 * — the Calendar layout — keeps the grid so it fills the height.
 */
export function isStackedMacroLayout(layout: Pick<Layout, "sections">): boolean {
  return layout.sections.length > 1 && layout.sections.every((s) => isMacroKind(s.kind));
}

/**
 * A stacked section's height **scale** (owner, 2026-11-01): the bottom edge is
 * dragged and the section's charts grow or shrink with it. A multiple of each
 * chart's default height, stored in the section's config as `scale`; widths are
 * still measured and text is never scaled.
 */
export const MACRO_SCALE_MIN = 0.6;
export const MACRO_SCALE_MAX = 2.5;
/** What a released drag is rounded to. */
export const MACRO_SCALE_STEP = 0.05;

/**
 * The kinds that carry a `scale`. Not the calendar: it fills whatever height it
 * is given (its day grid is `1fr` rows), so there is no chart height to scale.
 */
export type ScalableMacroKind = Exclude<MacroKind, "calendar">;

export function isScalableMacroKind(kind: string): kind is ScalableMacroKind {
  return isMacroKind(kind) && kind !== "calendar";
}

export function clampMacroScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.min(MACRO_SCALE_MAX, Math.max(MACRO_SCALE_MIN, scale));
}

/** Clamped and rounded to `MACRO_SCALE_STEP` — the value a release stores. */
export function roundMacroScale(scale: number): number {
  const stepped = Math.round(clampMacroScale(scale) / MACRO_SCALE_STEP) * MACRO_SCALE_STEP;
  return clampMacroScale(Math.round(stepped * 100) / 100);
}

/**
 * The live scale mid-drag: the section's height at the press, `startHeight`,
 * grown by `dy`, as a multiple of what it was — applied to the scale it had.
 * Clamped, not rounded (the drag is step-free until release).
 */
export function dragMacroScale(startScale: number, startHeight: number, dy: number): number {
  if (!(startHeight > 0)) return clampMacroScale(startScale);
  return clampMacroScale((startScale * (startHeight + dy)) / startHeight);
}

/** A config's scale, 1 when absent or not a usable number. */
export function macroScale(config: { scale?: number } | undefined): number {
  const s = config?.scale;
  return typeof s === "number" && Number.isFinite(s) && s > 0 ? clampMacroScale(s) : 1;
}

/** `config` with `scale` set — dropped when it is 1, the default. */
export function withMacroScale<C extends { scale?: number }>(config: C, scale: number): C {
  const { scale: _old, ...rest } = config;
  void _old;
  const s = roundMacroScale(scale);
  return (s === 1 ? rest : { ...rest, scale: s }) as C;
}
